import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2, XCircle, TrendingUp, Crown, Zap } from "lucide-react";

export default function PaymentSuccess() {
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [status, setStatus] = useState("checking"); // checking | paid | failed | timeout | error
  const [plan, setPlan] = useState(null);
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    const sid = new URLSearchParams(window.location.search).get("session_id");
    if (!sid) { setStatus("error"); return; }

    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data.payment_status === "paid") {
          setPlan(data.plan);
          setStatus("paid");
          await checkAuth();
          return;
        }
        if (data.status === "expired" || data.payment_status === "failed") {
          setStatus("failed");
          return;
        }
      } catch (e) { console.debug("payment status poll retry", e); }
      if (attempts >= 12) { setStatus("timeout"); return; }
      setTimeout(poll, 2000);
    };
    poll();
  }, [checkAuth]);

  const PlanIcon = plan === "elite" ? Crown : Zap;

  return (
    <div className="min-h-screen bg-[#070A0E] text-white grid place-items-center px-4">
      <div className="card-surface rounded-2xl p-10 max-w-md w-full text-center">
        {status === "checking" && (
          <>
            <Loader2 className="h-12 w-12 text-brand animate-spin mx-auto mb-5" />
            <h1 className="font-heading text-2xl font-extrabold">Confirming your payment…</h1>
            <p className="text-slate-400 text-sm mt-2">Hang tight, this only takes a moment.</p>
          </>
        )}

        {status === "paid" && (
          <div data-testid="payment-success-state">
            <div className="h-16 w-16 rounded-full bg-gain/15 border border-gain/30 grid place-items-center mx-auto mb-5">
              <CheckCircle2 className="h-9 w-9 text-gain" />
            </div>
            <h1 className="font-heading text-3xl font-black tracking-tight">You're in.</h1>
            <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-brand/30 bg-brand/10">
              <PlanIcon className="h-4 w-4 text-brand" />
              <span className="font-data text-xs uppercase tracking-widest text-brand">
                Netroy {plan === "elite" ? "Elite" : "Pro"} activated
              </span>
            </div>
            <p className="text-slate-400 text-sm mt-4">
              {plan === "elite" ? "Unlimited AI chart analyses are now unlocked." : "5 AI chart analyses per day are now unlocked."}
            </p>
            <div className="flex gap-3 mt-7">
              <Button data-testid="payment-goto-analyzer" onClick={() => navigate("/#analyzer")} className="flex-1 bg-brand hover:bg-brand-hover text-white font-semibold">Analyze a Chart</Button>
              <Button data-testid="payment-goto-dashboard" onClick={() => navigate("/dashboard")} variant="outline" className="flex-1 bg-transparent border-white/15 hover:bg-white/5 text-white">Dashboard</Button>
            </div>
          </div>
        )}

        {(status === "failed" || status === "error") && (
          <div data-testid="payment-failed-state">
            <div className="h-16 w-16 rounded-full bg-loss/15 border border-loss/30 grid place-items-center mx-auto mb-5">
              <XCircle className="h-9 w-9 text-loss" />
            </div>
            <h1 className="font-heading text-2xl font-extrabold">Payment not completed</h1>
            <p className="text-slate-400 text-sm mt-2">Something went wrong or the session expired. You have not been charged.</p>
            <Button onClick={() => navigate("/#pricing")} className="mt-6 bg-brand hover:bg-brand-hover text-white font-semibold">Back to Pricing</Button>
          </div>
        )}

        {status === "timeout" && (
          <div data-testid="payment-timeout-state">
            <Loader2 className="h-12 w-12 text-amber mx-auto mb-5" />
            <h1 className="font-heading text-2xl font-extrabold">Still processing</h1>
            <p className="text-slate-400 text-sm mt-2">Your payment is taking longer than usual. It will unlock automatically once confirmed.</p>
            <Button onClick={() => navigate("/dashboard")} className="mt-6 bg-brand hover:bg-brand-hover text-white font-semibold">Go to Dashboard</Button>
          </div>
        )}

        <button onClick={() => navigate("/")} className="mt-6 inline-flex items-center gap-1.5 font-data text-[11px] uppercase tracking-widest text-slate-500 hover:text-brand">
          <TrendingUp className="h-3.5 w-3.5" /> Netroy Forex Academy
        </button>
      </div>
    </div>
  );
}
